import type { ReactNode } from 'react'
import { cx } from '~/lib/cx'
import { Button } from './Button'

/**
 * The three things a screen shows instead of content: loading, nothing yet, and broken.
 * They live together because they are one decision — every list in this console picks
 * exactly one of them — and because keeping them apart is how a codebase ends up with
 * six spinners and one screen that says "No data".
 */

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={cx(
        'inline-block h-3 w-3 animate-spin rounded-full border border-line border-t-signal',
        className,
      )}
    />
  )
}

export function Skeleton({ className }: { className?: string }) {
  return <span className={cx('block animate-pulse rounded-xs bg-raised', className)} />
}

export function LoadingRows({ rows = 5 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-2 p-3" aria-busy>
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className="h-5" />
      ))}
    </div>
  )
}

export interface EmptyStateProps {
  title: string
  /** What to do next. An empty screen is an invitation to act, not a status report. */
  children?: ReactNode
  action?: ReactNode
}

export function EmptyState({ title, children, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
      <p className="label text-text">{title}</p>
      {children ? <p className="max-w-sm text-xs text-dim">{children}</p> : null}
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  )
}

export interface ErrorStateProps {
  /** What went wrong, in the words the backend used. Never "an error occurred". */
  message: string
  onRetry?: () => void
}

export function ErrorState({ message, onRetry }: ErrorStateProps) {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
      <p className="label text-fault">Could not load this</p>
      <p className="max-w-sm text-xs text-dim">{message}</p>
      {onRetry ? (
        <Button size="sm" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  )
}

/** An inline failure beside the control that caused it — a rejected mutation. */
export function InlineError({ message }: { message: string | null | undefined }) {
  if (!message) return null
  return (
    <p role="alert" className="rounded-sm border border-fault/40 bg-fault/10 px-2 py-1 text-xs text-fault">
      {message}
    </p>
  )
}

/**
 * Something the operator should read before they commit, not after — `signal` rather than
 * `fault`, because nothing is broken: the consequence is simply larger than the click.
 */
export function InlineWarning({ message }: { message: ReactNode }) {
  if (!message) return null
  return (
    <p className="rounded-sm border border-signal/40 bg-signal/10 px-2 py-1 text-xs text-signal">
      {message}
    </p>
  )
}

/** A quiet confirmation beside the control that caused it. */
export function InlineNote({ message }: { message: ReactNode }) {
  if (!message) return null
  return (
    <p className="rounded-sm border border-live/40 bg-live/10 px-2 py-1 text-xs text-live">
      {message}
    </p>
  )
}
